import { useContext, useMemo } from 'react';
import { useFormikContext, Field } from 'formik';
import { FaSpinner } from 'react-icons/fa';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import propTypes from 'prop-types';
import { FormGroup, Input, Label, CustomInput } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import { ErrorMessage } from '@kiper/ui';
import { ButtonLink, Subtitle } from '../Login/styles';
import context from '../../../services/auth/context';
import {
  StyledForm,
  InputWrapper,
  ToggleVisibilityButton,
  ButtonSubmit,
} from './styles';
import { usePasswordValidation, usePasswordVisibility } from '../hooks';

const PASSWORD_FIELDS = [
  'currentPassword',
  'newPassword',
  'passwordConfirmation',
];

const RenewPasswordForm = ({ mfaCodeRequired }) => {
  const formikBag = useFormikContext();
  const auth = useContext(context);
  const [t] = useTranslation('renew-password');
  const history = useHistory();

  const {
    customErrors,
    validatePassword,
    verifyCheckErrors,
  } = usePasswordValidation();
  const { visibility, toggleVisibility } = usePasswordVisibility(
    PASSWORD_FIELDS,
  );

  const { handleSubmit, handleChange, values, errors, touched } = formikBag;

  const goToLogin = () =>
    history.push({
      pathname: '/',
      search: `?email=${encodeURIComponent(values.username)}`,
    });

  const submitting = useMemo(() => auth.submitting, [auth]);

  const renderVisibilityToggle = field => (
    <ToggleVisibilityButton onClick={() => toggleVisibility(field)}>
      {visibility[field] ? <FiEyeOff size={18} /> : <FiEye size={18} />}
    </ToggleVisibilityButton>
  );

  return (
    <StyledForm onSubmit={handleSubmit} noValidate>
      <FormGroup>
        <CustomInput
          type="checkbox"
          id="check-lowercase"
          label={t('check.lowercase')}
          disabled
          checked={!customErrors.lowercase}
        />
        <CustomInput
          type="checkbox"
          id="check-uppercase"
          label={t('check.uppercase')}
          disabled
          checked={!customErrors.uppercase}
        />
        <CustomInput
          type="checkbox"
          id="check-number"
          label={t('check.number')}
          disabled
          checked={!customErrors.number}
        />
        <CustomInput
          type="checkbox"
          id="check-special-character"
          label={t('check.special-character')}
          disabled
          checked={!customErrors.specialCharacter}
        />
        <CustomInput
          type="checkbox"
          id="check-min-amount"
          label={t('check.min-amount')}
          disabled
          checked={!customErrors.minAmount}
        />
      </FormGroup>
      <FormGroup>
        <Label htmlFor="currentPassword">{t('label-current')}</Label>
        <InputWrapper>
          <Input
            type={visibility.currentPassword ? 'text' : 'password'}
            autoComplete="current-password"
            id="currentPassword"
            name="currentPassword"
            onChange={handleChange}
            required
            value={values.currentPassword}
            invalid={touched.currentPassword && !!errors.currentPassword}
          />
          {renderVisibilityToggle('currentPassword')}
        </InputWrapper>
        {touched.currentPassword && !!errors.currentPassword && (
          <ErrorMessage>{t('errors.current-password')}</ErrorMessage>
        )}
      </FormGroup>
      <FormGroup>
        <Label htmlFor="newPassword">{t('label')}</Label>
        <InputWrapper>
          <Field
            autoComplete="new-password"
            name="newPassword"
            type={visibility.newPassword ? 'text' : 'password'}
            validate={validatePassword}
            className={classnames(
              'form-control',
              touched.newPassword &&
                Object.values(customErrors).some(x => x) &&
                'is-invalid',
            )}
          />
          {renderVisibilityToggle('newPassword')}
        </InputWrapper>
        {touched?.newPassword && (
          <ErrorMessage>
            {customErrors.required && t('errors.password')}
            {verifyCheckErrors() && t('errors.others')}
          </ErrorMessage>
        )}
      </FormGroup>
      <FormGroup>
        <Label htmlFor="passwordConfirmation">{t('label-confirm')}</Label>
        <InputWrapper>
          <Input
            type={visibility.passwordConfirmation ? 'text' : 'password'}
            autoComplete="new-password"
            id="passwordConfirmation"
            name="passwordConfirmation"
            onChange={handleChange}
            required
            value={values.passwordConfirmation}
            invalid={
              touched.passwordConfirmation && !!errors.passwordConfirmation
            }
          />
          {renderVisibilityToggle('passwordConfirmation')}
        </InputWrapper>
        {touched?.passwordConfirmation && !!errors?.passwordConfirmation && (
          <ErrorMessage>{t('errors.password-confirmation')}</ErrorMessage>
        )}
      </FormGroup>
      {mfaCodeRequired && (
        <FormGroup>
          <Subtitle>{t('mfa.info')}</Subtitle>
          <Label htmlFor="mfaCode">{t('mfa.label')}</Label>
          <Input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            id="mfaCode"
            name="mfaCode"
            maxLength={6}
            onChange={handleChange}
            required
            value={values.mfaCode}
            invalid={touched.mfaCode && !!errors.mfaCode}
          />
          {touched.mfaCode && !!errors.mfaCode && (
            <ErrorMessage>{t('errors.mfa-code')}</ErrorMessage>
          )}
        </FormGroup>
      )}
      <ButtonSubmit
        block
        color="primary"
        type="submit"
        loading={submitting ? 1 : 0}
      >
        {submitting ? t('submitting') : t('submit')}
        {submitting && <FaSpinner size={14} />}
      </ButtonSubmit>
      <ButtonLink block color="link" onClick={goToLogin}>
        {t('back-to-login')}
      </ButtonLink>
    </StyledForm>
  );
};

export default RenewPasswordForm;

RenewPasswordForm.propTypes = {
  mfaCodeRequired: propTypes.bool,
};

RenewPasswordForm.defaultProps = {
  mfaCodeRequired: false,
};
